import { useEffect, useState, useCallback, useMemo } from "react";
import { api, errorMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { PlantillaCard } from "@/components/PlantillaCard";
import { PlantillaMaestraBloque } from "@/components/PlantillaMaestraBloque";
import { PlantillaFormDialog } from "@/components/PlantillaFormDialog";
import { GestionCatalogosDialog } from "@/components/GestionCatalogosDialog";
import { CargarEnCotizacionDialog } from "@/components/CargarEnCotizacionDialog";
import { GenerarPlantillasIADialog, InstruccionesIADialog } from "@/components/GenerarPlantillasIADialog";
import { AlcanceGeneracionDialog } from "@/components/AlcanceGeneracionDialog";
import { PropagacionDialog } from "@/components/PropagacionDialog";
import { SelectorSincronizacionDialog } from "@/components/SelectorSincronizacionDialog";
import { RepararHuerfanaDialog } from "@/components/RepararHuerfanaDialog";
import { HerenciaRotaDialog } from "@/components/HerenciaRotaDialog";
import { AgruparEquivalentesDialog } from "@/components/AgruparEquivalentesDialog";
import { AsignarServicioDialog } from "@/components/AsignarServicioDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Settings2, BookOpen, GitCompareArrows, HeartPulse } from "lucide-react";
import { useBusquedaLive } from "@/lib/busquedaHeader";

const VISTAS = [
  { k: "todas", label: "Todas" },
  { k: "maestras", label: "Maestras" },
  { k: "generadas", label: "Generadas" },
  { k: "manuales", label: "Manuales" },
];

export default function Plantillas() {
  const { isAdmin } = useAuth();
  const [plantillas, setPlantillas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [repuestos, setRepuestos] = useState([]);
  const [q] = useBusquedaLive("Buscar en plantillas…");
  const [filtroCat, setFiltroCat] = useState("todas");
  const [filtroModelo, setFiltroModelo] = useState("todos");
  const [vista, setVista] = useState("todas");
  const [agrupar, setAgrupar] = useState(false);
  const [soloSinServicio, setSoloSinServicio] = useState(false);
  const [asignarServicio, setAsignarServicio] = useState(null);
  const [expandida, setExpandida] = useState(null);
  const [detalles, setDetalles] = useState({});
  const [formDialog, setFormDialog] = useState({ open: false, plantilla: null });
  const [gestDialog, setGestDialog] = useState(null);
  const [plantillaACargar, setPlantillaACargar] = useState(null);
  const [generarIA, setGenerarIA] = useState(null);
  const [alcance, setAlcance] = useState(null);
  const [instruccionesOpen, setInstruccionesOpen] = useState(false);
  const [sincro, setSincro] = useState({ open: false, grupos: [], loading: false, error: null });
  const [sincroSelector, setSincroSelector] = useState({ open: false, hijas: [] });
  const [revisarHijas, setRevisarHijas] = useState(null); // confirmación liviana post-guardado
  const [propagMaestra, setPropagMaestra] = useState({ open: false, loading: false, error: null, grupos: [], maestraId: null, prefijos_removidos: [] });
  const [reparar, setReparar] = useState(null);
  const [saludOpen, setSaludOpen] = useState(false);
  const [saludKey, setSaludKey] = useState(0);
  const [agruparEquiv, setAgruparEquiv] = useState(null);

  // Paso 1: elegir qué plantillas generadas revisar. Se piden al backend por plantilla_maestra_id
  // (no dependemos del listado local, que puede no traer todas las hijas).
  const abrirSincronizacion = async () => {
    try {
      const { data } = await api.get("/plantillas/generadas/para-sincronizar");
      if (!data || data.length === 0) { toast.success("No hay plantillas generadas para sincronizar."); return; }
      setSincroSelector({ open: true, hijas: data });
    } catch (e) { toast.error(errorMsg(e)); }
  };

  // Paso 2: abrir el diálogo YA con estado de carga y luego correr la preview.
  const continuarSincronizacion = async (hijaIds) => {
    setSincroSelector({ open: false, hijas: [] });
    setSincro({ open: true, grupos: [], loading: true, error: null });
    try {
      const { data } = await api.post("/plantillas/sincronizacion/preview", { hija_ids: hijaIds });
      if (!data.grupos || data.grupos.length === 0) {
        setSincro({ open: false, grupos: [], loading: false, error: null });
        toast.success("Las plantillas seleccionadas ya están al día.");
        return;
      }
      setSincro({ open: true, grupos: data.grupos, loading: false, error: null });
    } catch (e) { setSincro((s) => ({ ...s, loading: false, error: errorMsg(e) })); }
  };

  // Post-guardado de maestra: abrir el diálogo YA con carga y calcular el preview aparte.
  const abrirPreviewMaestra = async (info) => {
    setRevisarHijas(null);
    setPropagMaestra({ open: true, loading: true, error: null, grupos: [],
      maestraId: info.id, prefijos_removidos: info.prefijos_removidos, nombre: info.nombre, hijasCount: info.hijasCount });
    try {
      const { data } = await api.post(`/plantillas/${info.id}/propagacion/preview`, { prefijos_removidos: info.prefijos_removidos });
      if (!data.con_cambios) {
        setPropagMaestra((p) => ({ ...p, open: false }));
        toast.success("Las plantillas hijas ya están al día.");
        return;
      }
      setPropagMaestra((p) => ({ ...p, loading: false,
        grupos: [{ maestra_id: info.id, maestra_nombre: info.nombre, hijas: data.hijas }] }));
    } catch (e) { setPropagMaestra((p) => ({ ...p, loading: false, error: errorMsg(e) })); }
  };

  const cargar = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (filtroCat !== "todas") params.categoria_id = filtroCat;
    if (filtroModelo !== "todos") params.modelo_id = filtroModelo;
    const [p, c, m, r] = await Promise.all([
      api.get("/plantillas", { params }), api.get("/categorias"), api.get("/modelos"), api.get("/repuestos"),
    ]);
    setPlantillas(p.data); setCategorias(c.data); setModelos(m.data); setRepuestos(r.data);
    setDetalles({});
  }, [q, filtroCat, filtroModelo]);

  const conDetalle = (p) => (detalles[p.id] ? { ...p, ...detalles[p.id] } : p);

  const toggle = async (p) => {
    const nuevo = expandida === p.id ? null : p.id;
    setExpandida(nuevo);
    if (nuevo && !detalles[p.id]) {
      try {
        const { data } = await api.get(`/plantillas/${p.id}`);
        setDetalles((d) => ({ ...d, [p.id]: data }));
      } catch { /* detalle no disponible */ }
    }
  };

  useEffect(() => { const t = setTimeout(cargar, 300); return () => clearTimeout(t); }, [cargar]);

  const eliminar = async (p) => {
    let msg = "¿Eliminar esta plantilla?";
    if (p.es_maestra) {
      try {
        const { data } = await api.get(`/plantillas/${p.id}/dependientes`);
        const n = (data.hijas_count || 0) + (data.herederas_count || 0);
        if (n > 0) {
          msg = `${n} plantilla(s) dependen de esta: ${data.hijas_count} hija(s) y ${data.herederas_count} que la heredan como padre.\n\n` +
            "Si la borrás, se limpiarán esas referencias y las hijas quedarán huérfanas (no se borran). ¿Continuar?";
        }
      } catch { /* si falla el chequeo, seguimos con confirmación simple */ }
    }
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.delete(`/plantillas/${p.id}`);
      const extra = [
        data.referencias_limpiadas ? `${data.referencias_limpiadas} referencia(s) limpiada(s)` : null,
        data.hijas_huerfanas ? `${data.hijas_huerfanas} hija(s) huérfana(s)` : null,
      ].filter(Boolean).join(" · ");
      toast.success(`Plantilla eliminada${extra ? ` · ${extra}` : ""}`);
      cargar();
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const cardProps = useCallback((p) => ({
    p: conDetalle(p), isAdmin, expandida: expandida === p.id,
    onToggle: () => toggle(p),
    onEditar: () => setFormDialog({ open: true, plantilla: p }),
    onEliminar: () => eliminar(p),
    onCargar: () => setPlantillaACargar(conDetalle(p)),
    onRepararHuerfana: () => setReparar(conDetalle(p)),
    onAsignarServicio: () => setAsignarServicio({ id: p.id, nombre: p.nombre }),
  }), [detalles, expandida, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Paso 1: elegir modelos ANTES de buscar/generar. Preselecciona los faltantes.
  const onGenerarFaltantes = (maestra, modelosObjetivo) =>
    setAlcance({ id: maestra.id, nombre: maestra.nombre,
      preseleccion: (modelosObjetivo && modelosObjetivo.length) ? modelosObjetivo : [] });

  // Paso 2: con la selección confirmada, recién ahí se busca en catálogos y se genera.
  const onAlcanceConfirmado = (modelosSel) => {
    const a = alcance;
    setAlcance(null);
    if (a) setGenerarIA({ id: a.id, nombre: a.nombre, modelosObjetivo: modelosSel });
  };

  const { maestras, generadas, manuales, genPorMaestra, genHuerfanas } = useMemo(() => {
    const base = soloSinServicio
      ? plantillas.filter((p) => (p.origen === "maestra" || p.origen === "manual") && p.tiene_servicio === false)
      : plantillas;
    const maestras = base.filter((p) => p.origen === "maestra");
    const generadas = base.filter((p) => p.origen === "generada");
    const manuales = base.filter((p) => p.origen === "manual");
    const maestraIds = new Set(maestras.map((m) => m.id));
    const genPorMaestra = {};
    const genHuerfanas = [];
    generadas.forEach((g) => {
      if (g.plantilla_maestra_id && maestraIds.has(g.plantilla_maestra_id)) {
        (genPorMaestra[g.plantilla_maestra_id] = genPorMaestra[g.plantilla_maestra_id] || []).push(g);
      } else genHuerfanas.push(g);
    });
    return { maestras, generadas, manuales, genPorMaestra, genHuerfanas };
  }, [plantillas, soloSinServicio]);

  const conteo = { todas: plantillas.length, maestras: maestras.length, generadas: generadas.length, manuales: manuales.length };
  const rotasCount = plantillas.filter((p) => p.herencia_rota).length;

  const renderGrid = (lista) => {
    if (lista.length === 0) return null;
    const grupos = agrupar
      ? Object.entries(lista.reduce((acc, p) => {
          const key = p.categoria?.nombre || "Sin categoría";
          (acc[key] = acc[key] || []).push(p); return acc;
        }, {}))
      : [["", lista]];
    return grupos.map(([titulo, sub]) => (
      <div key={titulo} className="space-y-2">
        {titulo && <h3 className="font-heading font-bold text-xs uppercase text-muted-foreground">{titulo}</h3>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {sub.map((p) => <PlantillaCard key={p.id} {...cardProps(p)} />)}
        </div>
      </div>
    ));
  };

  const renderMaestras = () => (
    maestras.length === 0
      ? <div className="bg-white border border-border rounded-sm p-8 text-center text-muted-foreground text-sm">No hay plantillas maestras. Marca una plantilla como maestra desde su edición.</div>
      : <div className="space-y-3">
          {maestras.map((m) => (
            <PlantillaMaestraBloque key={m.id} maestra={m} generadas={genPorMaestra[m.id] || []}
              isAdmin={isAdmin} cardProps={cardProps} onGenerarFaltantes={onGenerarFaltantes} onReparar={cargar}
              onAgrupar={(maestra) => setAgruparEquiv({ id: maestra.id, nombre: maestra.nombre })} />
          ))}
        </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">Plantillas de reparación</h1>
          <p className="text-sm text-muted-foreground">{plantillas.length} plantillas</p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button data-testid="instrucciones-ia-btn" variant="outline" size="sm" onClick={() => setInstruccionesOpen(true)}>
              <BookOpen size={14} className="mr-1" /> Instrucciones IA
            </Button>
            <Button data-testid="gestionar-categorias-btn" variant="outline" size="sm" onClick={() => setGestDialog("categorias")}>
              <Settings2 size={14} className="mr-1" /> Categorías
            </Button>
            <Button data-testid="sincronizar-hijas-btn" variant="outline" size="sm" onClick={abrirSincronizacion}>
              <GitCompareArrows size={14} className="mr-1" /> Revisar / Sincronizar generadas
            </Button>
            <Button data-testid="herencia-rota-btn" variant="outline" size="sm"
              className={rotasCount > 0 ? "border-amber-400 text-amber-700 hover:bg-amber-50" : ""}
              onClick={() => { setSaludKey((k) => k + 1); setSaludOpen(true); }}>
              <HeartPulse size={14} className="mr-1" /> Salud
              {rotasCount > 0 && (
                <span data-testid="herencia-rota-count" className="ml-1 text-[10px] bg-amber-200 text-amber-900 rounded-full px-1.5 py-0.5 font-bold">
                  {rotasCount}
                </span>
              )}
            </Button>
            <Button data-testid="nueva-plantilla-btn" size="sm" onClick={() => setFormDialog({ open: true, plantilla: null })}>
              <Plus size={14} className="mr-1" /> Nueva plantilla
            </Button>
          </div>
        )}
      </div>

      <div className="inline-flex rounded-sm border border-border bg-white p-0.5 gap-0.5">
        {VISTAS.map((v) => (
          <button key={v.k} data-testid={`vista-tab-${v.k}`} onClick={() => setVista(v.k)}
            className={`px-3 py-1 text-sm rounded-sm font-medium transition-colors ${vista === v.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>
            {v.label} <span className="text-xs opacity-70">({conteo[v.k]})</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <Select value={filtroCat} onValueChange={setFiltroCat}>
          <SelectTrigger data-testid="filtro-categoria-plantilla" className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas las categorías</SelectItem>
            {categorias.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filtroModelo} onValueChange={setFiltroModelo}>
          <SelectTrigger data-testid="filtro-modelo-plantilla" className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los modelos</SelectItem>
            {modelos.map((m) => <SelectItem key={m.id} value={m.id}>{m.nombre_mostrar || `${m.marca} ${m.modelo}`}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Checkbox data-testid="agrupar-checkbox" checked={agrupar} onCheckedChange={setAgrupar} /> Agrupar por categoría
        </label>
        <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Checkbox data-testid="solo-sin-servicio-checkbox" checked={soloSinServicio} onCheckedChange={setSoloSinServicio} /> Solo sin servicio
        </label>
      </div>

      {plantillas.length === 0 && (
        <div className="bg-white border border-border rounded-sm p-10 text-center text-muted-foreground text-sm">
          No hay plantillas. {isAdmin ? "Crea la primera plantilla de reparación." : "Un administrador debe crearlas."}
        </div>
      )}

      {plantillas.length > 0 && vista === "todas" && (
        <div className="space-y-6">
          {maestras.length > 0 && (
            <section className="space-y-2" data-testid="seccion-maestras">
              <h2 className="font-heading font-bold text-sm uppercase text-amber-700">Maestras</h2>
              {renderMaestras()}
            </section>
          )}
          {genHuerfanas.length > 0 && (
            <section className="space-y-2" data-testid="seccion-generadas-huerfanas">
              <h2 className="font-heading font-bold text-sm uppercase text-indigo-700">Generadas sin maestra</h2>
              {renderGrid(genHuerfanas)}
            </section>
          )}
          {manuales.length > 0 && (
            <section className="space-y-2" data-testid="seccion-manuales">
              <h2 className="font-heading font-bold text-sm uppercase text-slate-600">Manuales</h2>
              {renderGrid(manuales)}
            </section>
          )}
        </div>
      )}

      {plantillas.length > 0 && vista === "maestras" && renderMaestras()}

      {plantillas.length > 0 && vista === "generadas" && (
        generadas.length === 0
          ? <div className="bg-white border border-border rounded-sm p-8 text-center text-muted-foreground text-sm">No hay plantillas generadas por IA.</div>
          : renderGrid(generadas)
      )}

      {plantillas.length > 0 && vista === "manuales" && (
        manuales.length === 0
          ? <div className="bg-white border border-border rounded-sm p-8 text-center text-muted-foreground text-sm">No hay plantillas manuales.</div>
          : renderGrid(manuales)
      )}

      <PlantillaFormDialog open={formDialog.open} plantilla={formDialog.plantilla}
        onOpenChange={(v) => setFormDialog({ open: v, plantilla: v ? formDialog.plantilla : null })}
        categorias={categorias} modelos={modelos} repuestos={repuestos} onSaved={cargar}
        onRevisarHijas={(info) => setRevisarHijas(info)} />

      <GestionCatalogosDialog tipo={gestDialog} onClose={() => setGestDialog(null)}
        categorias={categorias} modelos={modelos} onChanged={cargar} />

      <CargarEnCotizacionDialog plantilla={plantillaACargar} onClose={() => setPlantillaACargar(null)} />

      <AlcanceGeneracionDialog open={!!alcance} maestraId={alcance?.id} maestraNombre={alcance?.nombre}
        preseleccion={alcance?.preseleccion} onClose={() => setAlcance(null)} onConfirm={onAlcanceConfirmado} />

      <GenerarPlantillasIADialog open={!!generarIA} maestraId={generarIA?.id} maestraNombre={generarIA?.nombre}
        modelosObjetivo={generarIA?.modelosObjetivo}
        onClose={() => setGenerarIA(null)} onDone={cargar} />

      <InstruccionesIADialog open={instruccionesOpen} onClose={() => setInstruccionesOpen(false)} />

      <SelectorSincronizacionDialog open={sincroSelector.open} hijas={sincroSelector.hijas}
        onClose={() => setSincroSelector({ open: false, hijas: [] })} onContinue={continuarSincronizacion} />

      <PropagacionDialog open={sincro.open} grupos={sincro.grupos}
        loading={sincro.loading} error={sincro.error}
        loadingText="Calculando cambios de las plantillas seleccionadas… esto puede tardar unos segundos."
        titulo="Revisar / Sincronizar plantillas generadas"
        aplicarFn={(seleccion) => api.post("/plantillas/sincronizacion/aplicar", { seleccion })}
        onClose={() => setSincro({ open: false, grupos: [], loading: false, error: null })} onDone={cargar} />

      <AlertDialog open={!!revisarHijas} onOpenChange={(o) => { if (!o) setRevisarHijas(null); }}>
        <AlertDialogContent data-testid="revisar-hijas-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Actualizar plantillas hijas</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Deseás revisar y actualizar las {revisarHijas?.hijasCount} plantilla{revisarHijas?.hijasCount === 1 ? "" : "s"} hija{revisarHijas?.hijasCount === 1 ? "" : "s"} con este cambio?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="revisar-hijas-ahora-no">Ahora no</AlertDialogCancel>
            <AlertDialogAction data-testid="revisar-hijas-si" onClick={() => abrirPreviewMaestra(revisarHijas)}>
              Sí, revisar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PropagacionDialog open={propagMaestra.open} grupos={propagMaestra.grupos}
        loading={propagMaestra.loading} error={propagMaestra.error}
        loadingText={`Calculando cambios para ${propagMaestra.hijasCount || ""} plantillas hijas… esto puede tardar unos segundos.`}
        titulo="¿Actualizar las plantillas hijas con este cambio?"
        aplicarFn={(seleccion) => api.post(`/plantillas/${propagMaestra.maestraId}/propagacion/aplicar`,
          { prefijos_removidos: propagMaestra.prefijos_removidos, hija_ids: seleccion.map((s) => s.hija_id),
            omitir: seleccion.map((s) => ({ hija_id: s.hija_id, omitir_agregar: s.omitir_agregar, omitir_quitar: s.omitir_quitar, omitir_opcional: s.omitir_opcional, omitir_cantidad: s.omitir_cantidad, split_omitir: s.split_omitir })) })}
        onClose={() => setPropagMaestra({ open: false, loading: false, error: null, grupos: [], maestraId: null, prefijos_removidos: [] })}
        onDone={cargar} />

      <RepararHuerfanaDialog plantilla={reparar} maestras={maestras}
        onClose={() => setReparar(null)} onDone={cargar} />

      <HerenciaRotaDialog open={saludOpen} maestras={maestras} refreshKey={saludKey}
        onClose={() => setSaludOpen(false)}
        onRepararHuerfana={(p) => { setSaludOpen(false); setReparar(p); }} />

      <AgruparEquivalentesDialog open={!!agruparEquiv} maestraId={agruparEquiv?.id} maestraNombre={agruparEquiv?.nombre}
        onClose={() => setAgruparEquiv(null)} onDone={cargar} />

      <AsignarServicioDialog open={!!asignarServicio} plantillaId={asignarServicio?.id} plantillaNombre={asignarServicio?.nombre}
        onClose={() => setAsignarServicio(null)}
        onDone={() => { setAsignarServicio(null); cargar(); }} />
    </div>
  );
}
